import { useMemo } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { Table } from 'foundations-components/transitional/components/Table';
import { Link } from 'foundations-components/transitional/components/Link';
import { List } from 'foundations-components/transitional/components/List';
import Log from 'forms-utils-lib/logger/Logger';
import { getContentDetailsRoute, getContentEditorRoute } from 'ui-forms-components/usageList/utils';
import COS_SUBCATEGORY from 'ui-forms-components/constants/COSSubcategory';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 72;
const OVERFLOW_HEIGHT = ROW_HEIGHT * 0.4;
const VISIBLE_ROWS = 4;
const SCROLLABLE_CONTAINER_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * VISIBLE_ROWS + OVERFLOW_HEIGHT;
const ScrollableContainer = styled.div.withConfig({
  displayName: "CosUsageList__ScrollableContainer"
})(["position:relative;"]);
const ScrollableContainerContents = styled.div.withConfig({
  displayName: "CosUsageList__ScrollableContainerContents"
})(["max-height:", "px;overflow-y:auto;"], SCROLLABLE_CONTAINER_HEIGHT);
const getCosTypeText = objectType => {
  switch (objectType) {
    case COS_SUBCATEGORY.LANDING_PAGE:
      return I18n.text('cosUsageList.cosTypes.landingPage');
    case COS_SUBCATEGORY.SITE_PAGE:
      return I18n.text('cosUsageList.cosTypes.sitePage');
    case COS_SUBCATEGORY.BLOG_POST:
      return I18n.text('cosUsageList.cosTypes.blogPost');
    case COS_SUBCATEGORY.BLOG_LISTING_PAGE:
      return I18n.text('cosUsageList.cosTypes.blogListingPage');
    case COS_SUBCATEGORY.WEB_INTERACTIVE:
      return I18n.text('cosUsageList.cosTypes.webInteractive');
    case COS_SUBCATEGORY.PORTAL_CONTENT:
      return I18n.text('cosUsageList.cosTypes.portalContent');
    default:
      {
        Log.warn(`Could not get COS type for unknown object type '${objectType}'.`);
        return I18n.text('cosUsageList.cosTypes.unknown');
      }
  }
};
const CosUsagePage = ({
  page,
  LinkRenderer,
  route
}) => {
  const {
    id,
    label,
    subType
  } = page;
  const routeGetter = route === 'details' ? getContentDetailsRoute : getContentEditorRoute;
  return /*#__PURE__*/_jsx("tr", {
    children: /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [LinkRenderer ? /*#__PURE__*/_jsx(LinkRenderer, {
          href: routeGetter(id, subType),
          children: label
        }) : /*#__PURE__*/_jsx(Link, {
          "data-test-id": "cos-page-links",
          use: "blue",
          href: routeGetter(id, subType),
          children: label
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cosUsageList.listItemAdditionalInfo",
            options: {
              contentType: getCosTypeText(subType)
            }
          })
        })]
      })
    })
  }, id);
};
const CosUsagePages = ({
  header,
  pages,
  route,
  LinkRenderer
}) => {
  if (!pages.length) return /*#__PURE__*/_jsx(_Fragment, {});
  return /*#__PURE__*/_jsxs(Table, {
    marginBlockEnd: "300",
    children: [/*#__PURE__*/_jsx("thead", {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx("th", {
          children: header
        })
      })
    }), /*#__PURE__*/_jsx("tbody", {
      children: pages.map(page => /*#__PURE__*/_jsx(CosUsagePage, {
        page: page,
        LinkRenderer: LinkRenderer,
        route: route
      }, page.id))
    })]
  });
};
const CosUsageList = ({
  pages,
  route = 'details',
  LinkRenderer
}) => {
  const landingPages = pages.filter(({
    subType
  }) => subType !== COS_SUBCATEGORY.WEB_INTERACTIVE);
  const webInteractives = pages.filter(({
    subType
  }) => subType === COS_SUBCATEGORY.WEB_INTERACTIVE);
  const landingPagesTable = useMemo(() => /*#__PURE__*/_jsx(CosUsagePages, {
    pages: landingPages,
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cosUsageList.cosPageHeader"
    }),
    route: route,
    LinkRenderer: LinkRenderer
  }), [landingPages, LinkRenderer, route]);
  const webInteractivesTable = useMemo(() => /*#__PURE__*/_jsx(CosUsagePages, {
    pages: webInteractives,
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cosUsageList.cosWebInteractiveHeader"
    }),
    LinkRenderer: LinkRenderer,
    route: route
  }), [webInteractives, LinkRenderer, route]);
  return /*#__PURE__*/_jsx(ScrollableContainer, {
    children: /*#__PURE__*/_jsxs(ScrollableContainerContents, {
      children: [landingPagesTable, webInteractivesTable]
    })
  });
};
export default CosUsageList;